import React from 'react';
import { useTheme } from '@mui/material';
import { Box } from '../../Box';
import { InputContext } from './InputContext';
import { themePalette } from '../../../theme/theme.palette';

export type InputControlProps = {
  /** @ignore */
  hidden?: boolean;

  /** The style of the input */
  variant?: 'solid' | 'outline';

  /** Whether the input asoociated with the label has an error */
  invalid?: boolean;

  /** Whether the input is disabled */
  disabled?: boolean;

  /** Whether the input is required */
  required?: boolean;

  /** The border radius of the input */
  borderRadius?: string | number;

  children?: React.ReactNode;
};

const InputControl: React.FC<InputControlProps> = ({
  invalid = false,
  variant = 'outline',
  borderRadius = 'medium',
  children,
  disabled,
  required,
  hidden,
}) => {
  const theme = useTheme();
  const { tokens, componentTokens, typographyTokens } = themePalette(theme.palette.mode);
  const contextValue = React.useMemo(
    () => ({
      invalid,
      variant,
      disabled,
      required,
      hidden,
    }),
    [invalid, variant, disabled, required, hidden],
  );

  const borderColor = React.useMemo(() => {
    if (invalid) {
      return componentTokens.inputs.colors.error.stroke.resting;
    }
    return variant === 'outline'
      ? componentTokens.inputs.colors.normal.stroke.resting
      : tokens?.inputBackgroundSolid;
  }, [variant, invalid, componentTokens, tokens?.inputBackgroundSolid]);

  const getBackgroundColor = (state: 'resting' | 'hover' | 'selected') => {
    if (variant !== 'outline') {
      return tokens?.inputBackgroundSolid;
    }
    if (invalid) {
      return componentTokens.inputs.colors.error.background[state];
    }
    return componentTokens.inputs.colors.normal.background[state];
  };

  const getBorderColor = (state: 'hover' | 'selected') => {
    if (invalid) {
      return componentTokens.inputs.colors.error.stroke[state];
    }
    return componentTokens.inputs.colors.normal.stroke[state];
  };

  return (
    <Box
      minHeight={48}
      overflow="auto"
      position="relative"
      border="1px solid"
      transition="border-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms"
      backgroundColor={getBackgroundColor('resting')}
      borderRadius={borderRadius}
      borderColor={borderColor}
      aria-disabled={disabled}
      aria-hidden={hidden}
      hidden={hidden}
      _hover={{
        borderColor: getBorderColor('hover'),
        backgroundColor: getBackgroundColor('hover'),
      }}
      _focusWithin={{
        borderColor: getBorderColor('selected'),
        backgroundColor: getBackgroundColor('selected'),
        span: {
          opacity: 1,
          visibility: 'visible',
        },
        label: {
          fontWeight: typographyTokens.fontWeights.semibold,
          transform: 'translate(6px, 6px) scale(0.65)',
        },
      }}
    >
      <InputContext.Provider value={contextValue}>{children}</InputContext.Provider>
    </Box>
  );
};

export default InputControl;
